import { Button, PinField } from '@flywire/headlessui-tailwindcss';
import { LoadingDots } from 'components/ui/LoadingDots/LoadingDots';
import type { OtpContext } from 'services/payex/otp/types';
import { OtpChannel } from 'services/payex/otp/types';
import { createMarkup } from 'utils/createMarkup/createMarkup';
import { useTranslations } from 'utils/translations/useTranslations';
import { useOtpFlow } from './hooks/useOtpFlow';

type OtpVerificationFormProps = {
  isActive: boolean;
  email: string;
  phone?: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  channel: OtpChannel;
  onVerified: () => void;
  onBack?: () => void;
};

const OTP_LENGTH = 6;
const INPUT_CLASS =
  'flex-1 min-w-0 max-w-pin-w h-pin-h text-pin font-medium border-2 rounded';

const OtpVerificationForm = ({
  isActive,
  email,
  phone,
  senderName,
  recipientName,
  context,
  channel,
  onVerified,
  onBack,
}: OtpVerificationFormProps) => {
  const i18n = useTranslations();
  const {
    code,
    setCode,
    errorMessage,
    isLoading,
    resendCooldown,
    pinFieldRef,
    handleVerify,
    handleResend,
  } = useOtpFlow({
    isOpen: isActive,
    email,
    senderName,
    recipientName,
    context,
    channel,
    onVerified,
  });

  return (
    <div className="text-center space-y-6" data-testid="otp-form">
      <h2 className="text-xl font-medium text-slate-800 text-center">
        {i18n.t(channel === OtpChannel.Sms ? 'otp.sms.title' : 'otp.title')}
      </h2>
      <p
        className="text-sm text-slate-500 mb-6 [&>strong]:text-slate-800"
        data-testid="otp-description"
        dangerouslySetInnerHTML={createMarkup(
          channel === OtpChannel.Sms
            ? i18n.t('otp.sms.description', { phone: phone ?? '' })
            : i18n.t('otp.description', { email }),
        )}
      />

      <div className="flex flex-col" data-testid="otp-inputs">
        <PinField
          ref={pinFieldRef}
          id="otp-pin"
          value={code}
          onChange={setCode}
          className="flex-nowrap items-center gap-2"
        >
          {Array.from({ length: OTP_LENGTH }, (_, index) => (
            <PinField.Input
              key={index}
              className={INPUT_CLASS}
              disabled={isLoading}
              aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
              data-testid={`otp-input-${index}`}
            />
          ))}
          {errorMessage && <PinField.Error className="hidden" />}
        </PinField>
        {errorMessage && (
          <span
            className="text-sm text-red-600 text-left mt-2 animate-[fadeIn_0.2s_ease-in-out]"
            data-testid="otp-error"
          >
            {errorMessage}
          </span>
        )}
      </div>

      <p
        className="text-xs text-gray-600 mb-6"
        data-testid="otp-resend-section"
      >
        {i18n.t('otp.didntReceive')}{' '}
        {resendCooldown > 0 ? (
          <span data-testid="otp-resend-cooldown">
            {i18n.t('otp.resendCooldown', {
              seconds: String(resendCooldown),
            })}
          </span>
        ) : (
          <button
            type="button"
            className="bg-transparent border-none p-0 font-semibold text-blue-600 hover:underline cursor-pointer"
            onClick={handleResend}
            data-testid="otp-resend-button"
          >
            {i18n.t('otp.resend')}
          </button>
        )}
      </p>

      <footer>
        <Button
          tone="primary"
          size="full"
          data-testid="otp-verify-button"
          onClick={handleVerify}
          disabled={isLoading}
        >
          {isLoading ? (
            <LoadingDots className="h-6 items-center [&>span]:bg-white" />
          ) : (
            i18n.t('otp.verify')
          )}
        </Button>
        {onBack && (
          <button
            type="button"
            className="mt-4 text-sm font-semibold text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
            onClick={onBack}
            data-testid="otp-back-button"
          >
            {i18n.t('otp.backToSignup')}
          </button>
        )}
      </footer>
    </div>
  );
};

export { OtpVerificationForm };
export type { OtpVerificationFormProps };
